<script setup lang="ts">
import { ref, onMounted } from 'vue';

type TableHeader = { id: string; label: string; align?: string; sortable?: boolean };

const props = defineProps<{
	columns: Array<TableHeader>;
	ariaLabel?: string;
}>();

const emit = defineEmits<{
	(e: 'updateColumns', value: Array<TableHeader>): void;
}>();

const isColumnFilterOpen = ref(false);
const checkedColumns = ref(props.columns ? [...props.columns] : []);

const isColumnChecked = (columnId: string): boolean => {
	return !!checkedColumns.value?.find((col) => col.id === columnId);
};

const handleColumnToggle = (isChecked: boolean, column: TableHeader) => {
	const columnIndexInColumns = props.columns.findIndex((col) => col.id === column.id);
	const columnIndexInChecked = checkedColumns.value.findIndex((col) => col.id === column.id);

	if (isChecked && columnIndexInChecked === -1) {
		let composedIndex;

		if (columnIndexInColumns > checkedColumns.value?.length / 2) {
			composedIndex =
				columnIndexInColumns - (props.columns.length - checkedColumns.value.length - 1);
		} else {
			composedIndex = columnIndexInColumns;
		}

		checkedColumns.value.splice(composedIndex, 0, column);
	} else {
		checkedColumns.value.splice(columnIndexInChecked, 1);
	}

	if (props.ariaLabel)
		localStorage.setItem(props.ariaLabel, JSON.stringify(checkedColumns.value));

	emit('updateColumns', checkedColumns.value);
};

onMounted(() => {
	if (props.ariaLabel && localStorage.getItem(props.ariaLabel)) {
		checkedColumns.value = JSON.parse(localStorage.getItem(props.ariaLabel)!);

		emit('updateColumns', checkedColumns.value);
	}
});

const resetColumns = () => {
	if (props.ariaLabel && localStorage.getItem(props.ariaLabel))
		localStorage.removeItem(props.ariaLabel);

	checkedColumns.value = [...props.columns];
	emit('updateColumns', checkedColumns.value);
};
</script>
<template>
	<caption
		class="table-filters"
		data-test="table-filters"
		v-click-outside="() => (isColumnFilterOpen = false)"
	>
		<button
			:class="[
				'table-filters__columns-btn',
				{ 'table-filters__columns-btn--active': isColumnFilterOpen },
			]"
			aria-label="Filter columns"
			@click="isColumnFilterOpen = !isColumnFilterOpen"
		>
			<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
				<path
					d="M19.893 3.001H4c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2h15.893c1.103 0 2-.897 2-2V5a2.003 2.003 0 0 0-2-1.999zM8 19.001H4V8h4v11.001zm6 0h-4V8h4v11.001zm2 0V8h3.893l.001 11.001H16z"
				></path>
			</svg>
			<span>Columns</span>
		</button>
		<ul
			:class="[
				'table-filters__dropdown',
				{ 'table-filters__dropdown--visible': isColumnFilterOpen },
			]"
		>
			<li v-for="column in columns" :key="`column-${column.id}`" class="mt-1">
				<label v-if="column.label" class="flex gap-2 table-filters__dropdown__option">
					<input
						type="checkbox"
						:disabled="checkedColumns.length <= 3 && isColumnChecked(column.id)"
						:checked="isColumnChecked(column.id)"
						@change="handleColumnToggle($event.target.checked, column)"
					/>
					{{ column.label }}
				</label>
			</li>
			<button class="table-filters__dropdown__reset-btn" @click="resetColumns">
				Reset columns
			</button>
		</ul>
	</caption>
</template>
<style lang="scss" scoped>
.table-filters {
	padding: $spacing-2;
	position: relative;
	z-index: $z-index-1;

	&__columns-btn {
		color: $color-neutral-500;
		display: flex;
		align-items: center;
		gap: $spacing-2;
		height: 28px;
		font-size: $font-size-sm;
		font-weight: $font-weight-medium;
		border: $border-width-1 $border-style $color-neutral-200;
		border-radius: $border-radius-1;
		padding: $spacing-2;
		margin-left: auto;

		svg {
			fill: $color-neutral-400;
		}

		&--active {
			background-color: $color-neutral-50;
		}
	}

	&__dropdown {
		display: none;
		position: absolute;
		right: $spacing-3;
		top: 40px;
		min-width: 200px;
		min-height: 200px;
		background-color: $color-white;
		box-shadow: $box-shadow-popover;
		padding: $spacing-2 $spacing-3;
		border-radius: $border-radius-1;

		&--visible {
			display: block;
		}

		&__option {
			cursor: pointer;
			padding: $spacing-2;
			border-radius: $border-radius-1;

			@media (hover: hover) {
				&:hover:not([disabled]),
				&:active:not([disabled]) {
					background-color: $color-neutral-100;
					transition: background-color 300ms ease;
				}
			}
		}

		&__reset-btn {
			font-size: $font-size-sm;
			padding: $spacing-1;
			border-radius: $border-radius-1;
			background-color: $color-neutral-50;
			width: 100%;
			margin-top: $spacing-2;
			font-weight: $font-weight-medium;

			@media (hover: hover) {
				&:hover {
					color: $color-pending-400;
				}
			}
		}
	}
}
</style>
